import { LucideIcon } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface StatCardProps {
  title: string;
  value: string;
  description?: string;
  change?: string;
  changeType?: "increase" | "decrease" | "neutral";
  icon: LucideIcon;
  iconColor?: string;
  bgColor?: string;
  delay?: number;
  className?: string;
  variant?: "vertical" | "horizontal";
  loading?: boolean;
}

export function StatCard({
  title,
  value,
  description,
  change,
  changeType = "neutral",
  icon: Icon,
  iconColor = "text-primary",
  bgColor,
  delay = 0,
  className,
  variant = "vertical",
  loading = false,
}: StatCardProps) {
  // Extract color for glow effect
  const glowColorClass = iconColor.startsWith('text-') ? iconColor.replace('text-', 'from-') : 'from-primary';

  const commonClasses = cn(
    "stat-card group relative overflow-hidden transition-all duration-500",
    "motion-safe:hover:scale-[1.02] motion-safe:hover:-translate-y-1 motion-safe:hover:shadow-2xl",
    "backdrop-blur-sm bg-card/80 border-border/50 hover:border-primary/40",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background",
    "cursor-default select-none touch-manipulation active:scale-[0.98] transition-transform",
    className
  );

  const GlowEffect = () => (
    <div 
      className={cn(
        "absolute inset-0 bg-gradient-to-br to-transparent opacity-0 group-hover:opacity-10 transition-opacity duration-700 pointer-events-none",
        glowColorClass
      )} 
    />
  );

  const content = variant === "horizontal" ? (
    <div className="relative flex items-center gap-4">
      <div
        className={cn(
          "flex h-12 w-12 items-center justify-center rounded-2xl shrink-0 transition-all duration-500",
          "motion-safe:group-hover:rotate-12 motion-safe:group-hover:scale-110",
          bgColor || "bg-primary/10",
          iconColor
        )}
      >
        <Icon className="h-6 w-6" aria-hidden="true" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground/80 mb-0.5">{title}</p>
        <p className="text-2xl font-display font-black text-foreground tracking-tight">{value}</p>
        {description && (
          <p className="text-[10px] leading-tight text-muted-foreground/60 mt-1 line-clamp-1">{description}</p>
        )}
      </div>
    </div>
  ) : (
    <div className="relative flex flex-col items-center text-center gap-4">
      <div
        className={cn(
          "flex h-14 w-14 items-center justify-center rounded-3xl transition-all duration-500",
          "motion-safe:group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-primary/10",
          bgColor || "bg-primary/10",
          iconColor
        )}
      >
        <Icon className="h-7 w-7" aria-hidden="true" />
      </div>
      <div className="space-y-1 w-full">
        <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground/80">{title}</p>
        <p className="text-3xl font-display font-black text-foreground tracking-tight">{value}</p>
        {description && (
          <p className="text-xs text-muted-foreground/60">{description}</p>
        )}
        {change && (
          <p
            className={cn(
              "text-sm font-medium flex items-center justify-center gap-1",
              changeType === "increase" && "text-success",
              changeType === "decrease" && "text-destructive",
              changeType === "neutral" && "text-muted-foreground"
            )}
          >
            <span className="sr-only">
              {changeType === "increase" ? "Aumento de " : changeType === "decrease" ? "Disminución de " : "Cambio de "}
            </span>
            {changeType === "increase" && <span aria-hidden="true">↑</span>}
            {changeType === "decrease" && <span aria-hidden="true">↓</span>}
            {change}
          </p>
        )}
      </div>
    </div>
  );

  const ariaLabel = loading
    ? `${title}: cargando`
    : `${title}: ${value}${description ? `. ${description}` : ''}${change ? `. ${changeType === 'increase' ? 'Aumento de' : changeType === 'decrease' ? 'Disminución de' : 'Cambio de'} ${change}` : ''}`;

  const skeletonContent = variant === "horizontal" ? (
    <div className="relative flex items-center gap-4">
      <Skeleton className={cn("h-12 w-12 rounded-2xl shrink-0", bgColor || "bg-primary/10")} />
      <div className="min-w-0 flex-1 space-y-2">
        <Skeleton className="h-3 w-20" />
        <Skeleton className="h-7 w-24" />
        {description !== undefined && <Skeleton className="h-2.5 w-32" />}
      </div>
    </div>
  ) : (
    <div className="relative flex flex-col items-center text-center gap-4">
      <Skeleton className={cn("flex h-14 w-14 rounded-3xl", bgColor || "bg-primary/10")} />
      <div className="space-y-2 w-full flex flex-col items-center">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-9 w-20" />
        {(description !== undefined || change !== undefined) && <Skeleton className="h-4 w-32" />}
      </div>
    </div>
  );

  return (
    <div
      role="article"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-busy={loading}
      className={cn(commonClasses, "animate-slide-in-up", variant === "horizontal" ? "p-4 sm:p-5" : "p-6")}
      style={{ animationDelay: `${delay}ms` }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const el = e.currentTarget;
          el.classList.add('scale-[0.98]', 'ring-4', 'ring-primary/40');
          setTimeout(() => el.classList.remove('scale-[0.98]', 'ring-4', 'ring-primary/40'), 150);
        }
      }}
    >
      <GlowEffect />
      {loading ? skeletonContent : content}
    </div>
  );
}
